import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import { listMotivationMessages } from '../../services/reportService';
import { getCompanyProfile, updateCompanyProfile } from '../../services/companyService';

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
};

const PRESETS = [
  { label: 'Last 30 days', from: daysAgoStr(29), to: todayStr() },
  { label: 'Last 90 days', from: daysAgoStr(89), to: todayStr() },
  { label: 'Last 6 months', from: daysAgoStr(179), to: todayStr() },
];

const TIER_LABELS = { 1: 'Elite', 2: 'Rising', 3: 'Building' };
const TIER_BADGE  = { 1: 'badge-gold', 2: 'badge-info', 3: 'badge-success' };

function SummaryCard({ label, value, accent }) {
  return (
    <div className="panel" style={{ padding: '18px 20px' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color: accent ? 'var(--accent)' : 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

export default function MotivationMessagesReport() {
  const [filters, setFilters] = useState({
    from: daysAgoStr(89),
    to: todayStr(),
    driverId: '',
    tier: '',
    limit: '1000',
  });
  const [submitted, setSubmitted] = useState({ ...filters });
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [togglingFeature, setTogglingFeature] = useState(false);

  useEffect(() => {
    getCompanyProfile()
      .then((res) => {
        const enabled = res.data?.weeklyMotivationSettings?.enabled ?? true;
        setFeatureEnabled(enabled);
      })
      .catch(() => {});
  }, []);

  const toggleFeature = async () => {
    const next = !featureEnabled;
    setTogglingFeature(true);
    try {
      await updateCompanyProfile({ weeklyMotivationSettings: { enabled: next } });
      setFeatureEnabled(next);
    } catch {
      // silently revert — user will see no change
    } finally {
      setTogglingFeature(false);
    }
  };

  const runReport = useCallback(async (params) => {
    setLoading(true);
    setError('');
    try {
      const query = { limit: params.limit || 1000 };
      if (params.from) query.from = params.from;
      if (params.to) query.to = params.to;
      if (params.driverId?.trim()) query.driverId = params.driverId.trim();
      if (params.tier) query.tier = params.tier;
      const res = await listMotivationMessages(query);
      setLogs(res.data.logs || []);
      setTotal(res.data.total ?? 0);
      setSummary(res.data.summary || null);
      setHasRun(true);
    } catch {
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runReport(submitted); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted({ ...filters });
    runReport(filters);
  };

  const applyPreset = (p) => {
    const next = { ...filters, from: p.from, to: p.to };
    setFilters(next);
    setSubmitted(next);
    runReport(next);
  };

  const exportCsv = () => {
    const headers = [
      'Driver ID', 'Driver Name', 'Week Starting', 'Tier', 'Trips This Week',
      'Trips Last Week', 'Improved', 'Rank', 'Total Active Drivers',
      'Message Title', 'Message Body', 'FCM Delivered', 'Sent At',
    ];
    const rows = logs.map((l) => [
      l.driverId,
      l.driverName,
      l.weekStart ? new Date(l.weekStart).toISOString().slice(0, 10) : '',
      TIER_LABELS[l.tier] || l.tier,
      l.tripsThisWeek,
      l.tripsLastWeek,
      l.improved ? 'Yes' : 'No',
      l.rank ?? '',
      l.totalActiveDrivers,
      l.messageTitle,
      l.messageBody,
      l.fcmSuccess ? 'Yes' : 'No',
      l.sentAt ? new Date(l.sentAt).toISOString() : '',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `motivation-messages-${submitted.from}-to-${submitted.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const featureToggle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        {featureEnabled ? 'Enabled' : 'Disabled'}
      </span>
      <button
        type="button"
        onClick={toggleFeature}
        disabled={togglingFeature}
        aria-pressed={featureEnabled}
        style={{
          position: 'relative',
          width: 44,
          height: 24,
          borderRadius: 12,
          border: 'none',
          cursor: togglingFeature ? 'wait' : 'pointer',
          background: featureEnabled ? 'var(--accent, #3b82f6)' : 'var(--border, #334155)',
          transition: 'background 0.2s',
          flexShrink: 0,
          padding: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: featureEnabled ? 23 : 3,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.2s',
          }}
        />
      </button>
    </div>
  );

  return (
    <AppLayout
      title="Weekly Motivation Messages"
      subtitle="Log of every weekly performance notification sent to drivers."
      actions={featureToggle}
    >
      {/* Filters */}
      <div className="surface" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: '0.8rem' }}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="form-grid" style={{ maxWidth: 900 }}>
            <div>
              <label htmlFor="mm-from">From</label>
              <input
                id="mm-from"
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="mm-to">To</label>
              <input
                id="mm-to"
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="mm-driver">
                Driver ID <span style={{ color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <input
                id="mm-driver"
                type="text"
                value={filters.driverId}
                onChange={(e) => setFilters((p) => ({ ...p, driverId: e.target.value }))}
                placeholder="Filter by driver"
              />
            </div>
            <div>
              <label htmlFor="mm-tier">Tier</label>
              <select
                id="mm-tier"
                value={filters.tier}
                onChange={(e) => setFilters((p) => ({ ...p, tier: e.target.value }))}
              >
                <option value="">All tiers</option>
                <option value="1">Tier 1 — Elite</option>
                <option value="2">Tier 2 — Rising</option>
                <option value="3">Tier 3 — Building</option>
              </select>
            </div>
            <div>
              <label htmlFor="mm-limit">Limit</label>
              <select
                id="mm-limit"
                value={filters.limit}
                onChange={(e) => setFilters((p) => ({ ...p, limit: e.target.value }))}
              >
                <option value="500">500</option>
                <option value="1000">1 000</option>
                <option value="2000">2 000</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading…' : 'Run report'}
              </button>
              {hasRun && logs.length > 0 && (
                <button type="button" className="btn btn-ghost" onClick={exportCsv}>
                  Export CSV
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="feedback error" style={{ marginBottom: 16 }}>
          {error}{' '}
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: '0.8rem', marginLeft: 8 }}
            onClick={() => runReport(submitted)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Summary cards */}
      {hasRun && !loading && summary && logs.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <SummaryCard label="Total sent" value={total.toLocaleString()} />
          <SummaryCard label="Improved" value={summary.improved.toLocaleString()} accent />
          <SummaryCard label="Tier 1 — Elite" value={summary.tier1.toLocaleString()} />
          <SummaryCard label="Tier 2 — Rising" value={summary.tier2.toLocaleString()} />
          <SummaryCard label="Tier 3 — Building" value={summary.tier3.toLocaleString()} />
          <SummaryCard label="FCM Delivered" value={summary.fcmDelivered.toLocaleString()} />
        </div>
      )}

      {/* Table */}
      {hasRun && (
        <div className="surface">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0 }}>Sent messages</h3>
            {!loading && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {total.toLocaleString()} record{total !== 1 ? 's' : ''}
                {logs.length < total ? ` (showing first ${logs.length})` : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 12 }} />
          ) : logs.length === 0 ? (
            <div className="empty-state">No motivation messages found for the selected filters.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Week</th>
                    <th>Tier</th>
                    <th>Trips</th>
                    <th>vs Last Week</th>
                    <th>Rank</th>
                    <th>Message</th>
                    <th>FCM</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => {
                    const delta = l.tripsThisWeek - l.tripsLastWeek;
                    const deltaLabel = delta > 0 ? `+${delta}` : delta === 0 ? '—' : String(delta);
                    const deltaColor = delta > 0 ? 'var(--success)' : delta < 0 ? 'var(--danger)' : 'var(--text-secondary)';
                    return (
                      <tr key={l._id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{l.driverName}</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                            {l.driverId}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDate(l.weekStart)}
                        </td>
                        <td>
                          <span className={`badge ${TIER_BADGE[l.tier] || 'badge-secondary'}`}>
                            {TIER_LABELS[l.tier] || `Tier ${l.tier}`}
                          </span>
                        </td>
                        <td style={{ fontWeight: 700, textAlign: 'center' }}>
                          {l.tripsThisWeek}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: deltaColor }}>
                          {deltaLabel}
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                          {l.tier < 3 && l.rank != null
                            ? `#${l.rank} of ${l.totalActiveDrivers}`
                            : '—'}
                        </td>
                        <td style={{ maxWidth: 320 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 2 }}>
                            {l.messageTitle}
                          </div>
                          <div
                            style={{
                              fontSize: '0.78rem',
                              color: 'var(--text-secondary)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 300,
                            }}
                            title={l.messageBody}
                          >
                            {l.messageBody}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${l.fcmSuccess ? 'badge-success' : 'badge-warning'}`}>
                            {l.fcmSuccess ? 'Delivered' : l.fcmError === 'no_token' ? 'No token' : 'Failed'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {fmtDateTime(l.sentAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
